import { styled, SxProps, Theme } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import DialogContent from "@mui/material/DialogContent"
import React from "react"

interface SpinnerProps {
  className: string
  size: number
  zIndex: number
  thickness: number
  disableShrink: boolean
  dialog: boolean
  customStyles: SxProps<Theme>
}

const StyledCircularProgress = styled(CircularProgress, {
  shouldForwardProp: prop => prop !== "margin" && prop !== "zIndex",
})<{ margin: number; zIndex: number }>(({ margin, zIndex }) => ({
  marginLeft: margin,
  marginTop: margin,
  zIndex,
  left: "50%",
  position: "absolute",
  top: "50%",
}))

const StyledDialogContent = styled(DialogContent)(() => ({
  minHeight: 150,
}))

/**
 * A simple spinner component.
 *
 * @prop {string} [className=""] - The css class of the spinner.
 * @prop {number} [size=72] - The size of the spinner.
 * @prop {number} [zIndex=9] - The z index of the spinner.
 * @prop {number} [thickness=3.6] - The thickness of the spinner.
 * @prop {boolean} [disableShrink=true] - Whether to disable shrink or not.
 * @prop {boolean} [dialog=false] - Whether this is a dialog spinner.
 * @prop {object} [customStyles={}] - Custom styles for the spinner.
 */
const Spinner: React.FC<Partial<SpinnerProps>> = ({
  className = "",
  disableShrink = true,
  size = 72,
  thickness = 3.6,
  zIndex = 9,
  dialog = false,
  customStyles = {},
}) => {
  const margin = -(size / 2)
  const Wrapper = dialog ? StyledDialogContent : React.Fragment
  return (
    <Wrapper>
      <StyledCircularProgress
        sx={customStyles}
        className={className}
        size={size}
        variant="indeterminate"
        disableShrink={disableShrink}
        thickness={thickness}
        zIndex={zIndex}
        margin={margin}
      />
    </Wrapper>
  )
}

export default Spinner
